import React, { useContext, useMemo } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Container,
  Tooltip,
  Button,
} from "@mui/material";
import { PersonOutlineOutlined } from "@mui/icons-material";
import { useDimensions } from "src/utils/useDimensions.js";
import { useTheme } from "@emotion/react";
import maestroLogo from "src/assets/maestro-logo.png";
import maestroLogoWhite from "src/assets/maestro-logo-white.png";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { getPageRoute } from "src/services";
import { AuthContext } from "src/utils/AuthContext.js";

function MaestroNavbar({ variant = "light" }) {
  const theme = useTheme();
  const { isMediumScreen } = useDimensions();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const loggedIn = useMemo(() => {
    return !!user;
  }, [user]);

  const isDark = variant === "dark";
  const bgColor = isDark ? "#000000" : "white";
  const logoSrc = isDark ? maestroLogoWhite : maestroLogo;
  const signInColor = isDark
    ? theme.palette.paper.primary
    : theme.palette.text.primary;
  const iconColor = isDark
    ? theme.palette.paper.primary
    : theme.palette.icon?.primary || theme.palette.text.primary;

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: bgColor,
          zIndex: "4",
          transition: "opacity, height 0.1s linear",
          boxShadow: "none !important",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              margin: !isMediumScreen && "0rem 3rem",
              minHeight: { md: "48px" },
              py: { xs: 0, md: 0.5 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Maestro Logo */}
            <Box sx={{ display: "flex", alignItems: "center", mt: 1.5 }}>
              <img
                src={logoSrc}
                alt="Maestro"
                style={{
                  height: !isMediumScreen ? "50px" : "50px",
                  cursor: "pointer",
                }}
                onClick={() => navigate(getPageRoute("home", "HOME"))}
              />
            </Box>

            <Box sx={{ flexGrow: 1 }}></Box>

            {/* Right side buttons */}
            {loggedIn ? (
              <Box
                display="flex"
                alignItems="center"
                sx={{ flexGrow: 0 }}
              >
                <Tooltip title="Your Profile">
                  <IconButton
                    onClick={() => navigate(getPageRoute("maestro", "DASHBOARD"))}
                    size="small"
                  >
                    <PersonOutlineOutlined
                      fontSize="medium"
                      sx={{ color: iconColor }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to={getPageRoute("waitlist", "WAITLIST")}
                  variant="contained"
                  sx={{
                    bgcolor: theme.palette.success.main,
                    color: theme.palette.paper.primary,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    borderRadius: 999,
                    px: { xs: 2, md: 2.5 },
                    py: { xs: 0.75, md: 1 },
                    fontSize: { xs: "0.75rem", md: "0.8125rem" },
                    boxShadow: "none",
                    mr: 1.5,
                    "&:hover": {
                      bgcolor: "#15803d",
                      boxShadow: "0 4px 12px rgba(32,156,51,0.25)",
                    },
                  }}
                >
                  Join the waitlist →
                </Button>

                <Button
                  component={RouterLink}
                  to={getPageRoute("auth", "SIGNIN")}
                  variant="text"
                  sx={{
                    display: { xs: "none", md: "inline" },
                    color: signInColor,
                  }}
                >
                  Sign In
                </Button>

                <Tooltip title="Sign In">
                  <IconButton
                    component={RouterLink}
                    to={getPageRoute("auth", "SIGNIN")}
                    size="small"
                    sx={{ display: { xs: "flex", md: "none" } }}
                  >
                    <PersonOutlineOutlined
                      fontSize="medium"
                      sx={{ color: iconColor }}
                    />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
}

export default MaestroNavbar;